import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const CurrentPayrollConfirmation = ({ employeeData, currentPayrollNumber = null }) => {
  const [attendanceData, setAttendanceData] = useState(null);
  const [confirmationStatus, setConfirmationStatus] = useState('pending');
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandCalendar, setExpandCalendar] = useState(false);

  useEffect(() => {
    console.log('CurrentPayrollConfirmation useEffect triggered:', { currentPayrollNumber, employeeData });
    if (currentPayrollNumber) {
      fetchCurrentAttendance();
      checkConfirmationStatus();
    }
  }, [currentPayrollNumber, employeeData]);

  // Periodically check for status updates (every 30 seconds)
  useEffect(() => {
    if (!currentPayrollNumber) return;

    const interval = setInterval(() => {
      checkConfirmationStatus();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [currentPayrollNumber]);

  const fetchCurrentAttendance = async () => {
    try {
      setLoading(true);
      
      // Get current payroll period details
      const { data: periodData, error: periodError } = await supabase
        .from('payroll_period')
        .select('*')
        .eq('payroll_number', currentPayrollNumber)
        .single();

      if (periodError) {
        console.error('Error fetching period:', periodError);
        return;
      }
      
      console.log('Period data:', periodData);

      // Fetch attendance records for current period
      const { data: attendanceRecords, error: attendanceError } = await supabase
        .from('payroll_attendance')
        .select('*')
        .eq('employee_number', employeeData.employee_number)
        .eq('payroll_number', currentPayrollNumber)
        .order('date', { ascending: true });

      if (attendanceError) {
        console.error('Error fetching attendance:', attendanceError);
        return;
      }
      
      console.log('Attendance records:', attendanceRecords);

      // Fetch payroll summary for complete hour breakdown - check both companies
      const { data: payrollSummaryData, error: summaryError } = await supabase
        .from('payroll_summary')
        .select('*')
        .eq('employee_number', employeeData.employee_number)
        .eq('payroll_number', currentPayrollNumber)
        .limit(1);
      
      const payrollSummary = payrollSummaryData && payrollSummaryData.length > 0 ? payrollSummaryData[0] : null;
      
      if (summaryError) {
        console.error('Error fetching payroll summary:', summaryError);
      } else {
        console.log('Payroll summary:', payrollSummary);
      }

      // Fetch benefit hours from payroll_summary for both companies
      let kfBenefitHours = { pto_hp: 0, sick_leave: 0, lunch: 0 };
      let kooBenefitHours = { pto_hp: 0, sick_leave: 0, lunch: 0 };
      
      // Fetch KF company benefit hours from payroll_summary if employee has KF ID
      if (employeeData.employee_id_kf) {
        const { data: kfSummaryData, error: kfError } = await supabase
          .from('payroll_summary')
          .select('pto_hp, sick_leave, lunch')
          .eq('employee_number', employeeData.employee_number)
          .eq('company', 'KF')
          .eq('payroll_number', currentPayrollNumber)
          .limit(1);
        
        const kfSummary = kfSummaryData && kfSummaryData.length > 0 ? kfSummaryData[0] : null;

        if (kfError) {
          console.error('Error fetching KF benefit hours:', kfError);
        } else {
          console.log('KF benefit hours data:', kfSummary);
        }
        
        if (!kfError && kfSummary) {
          kfBenefitHours = {
            pto_hp: parseFloat(kfSummary.pto_hp || 0),
            sick_leave: parseFloat(kfSummary.sick_leave || 0),
            lunch: parseFloat(kfSummary.lunch || 0)
          };
        }
      }

      // Fetch KOO company benefit hours from payroll_summary if employee has KOO ID
      if (employeeData.employee_id_koo) {
        const { data: kooSummaryData, error: kooError } = await supabase
          .from('payroll_summary')
          .select('pto_hp, sick_leave, lunch')
          .eq('employee_number', employeeData.employee_number)
          .eq('company', 'KOO')
          .eq('payroll_number', currentPayrollNumber)
          .limit(1);
        
        const kooSummary = kooSummaryData && kooSummaryData.length > 0 ? kooSummaryData[0] : null;

        if (kooError) {
          console.error('Error fetching KOO benefit hours:', kooError);
        } else {
          console.log('KOO benefit hours data:', kooSummary);
        }
        
        if (!kooError && kooSummary) {
          kooBenefitHours = {
            pto_hp: parseFloat(kooSummary.pto_hp || 0),
            sick_leave: parseFloat(kooSummary.sick_leave || 0),
            lunch: parseFloat(kooSummary.lunch || 0)
          };
        }
      }

      let totalHours = 0;
      let regularHours = 0;
      let ptoHours = 0;
      let sickHours = 0;
      let lunchHours = 0;

      if (payrollSummary) {
        // For timesheet confirmation: calculate timesheet hours from calendar (combined view)
        regularHours = attendanceRecords.reduce((sum, record) => sum + (parseFloat(record.hours) || 0), 0);
        
        // Aggregate benefit hours from both companies
        ptoHours = kfBenefitHours.pto_hp + kooBenefitHours.pto_hp;
        sickHours = kfBenefitHours.sick_leave + kooBenefitHours.sick_leave;
        lunchHours = kfBenefitHours.lunch + kooBenefitHours.lunch;
        
        // If no benefit hours found from separate company queries, try aggregating from all payroll_summary records
        // This handles cases where benefit hours might be in any company's record
        if (ptoHours === 0 && sickHours === 0 && lunchHours === 0) {
          // Fetch all payroll_summary records for this employee and payroll number
          const { data: allSummaryData, error: allSummaryError } = await supabase
            .from('payroll_summary')
            .select('pto_hp, sick_leave, lunch, company')
            .eq('employee_number', employeeData.employee_number)
            .eq('payroll_number', currentPayrollNumber);
          
          if (!allSummaryError && allSummaryData && allSummaryData.length > 0) {
            // Aggregate benefit hours from all company records
            ptoHours = allSummaryData.reduce((sum, record) => sum + (parseFloat(record.pto_hp || 0)), 0);
            sickHours = allSummaryData.reduce((sum, record) => sum + (parseFloat(record.sick_leave || 0)), 0);
            lunchHours = allSummaryData.reduce((sum, record) => sum + (parseFloat(record.lunch || 0)), 0);
            console.log('Aggregated benefit hours from all payroll_summary records:', {
              records: allSummaryData,
              ptoHours,
              sickHours,
              lunchHours
            });
          } else {
            // Last resort: use the initial payrollSummary (might be from one company only)
            ptoHours = parseFloat(payrollSummary.pto_hp || 0);
            sickHours = parseFloat(payrollSummary.sick_leave || 0);
            lunchHours = parseFloat(payrollSummary.lunch || 0);
          }
        }
        
        // Total hours = timesheet hours (from calendar) + benefit hours (aggregated)
        totalHours = regularHours + ptoHours + sickHours + lunchHours;
        
        console.log('Benefit hours calculation:', {
          regularHours,
          ptoHours,
          sickHours,
          lunchHours,
          totalHours,
          kfBenefitHours,
          kooBenefitHours
        });
      } else {
        // Fallback to attendance records calculation
        totalHours = attendanceRecords.reduce((sum, record) => sum + (parseFloat(record.hours) || 0), 0);
        regularHours = attendanceRecords
          .filter(r => r.store_name !== 'PTO')
          .reduce((sum, record) => sum + (parseFloat(record.hours) || 0), 0);
        ptoHours = attendanceRecords
          .filter(r => r.store_name === 'PTO')
          .reduce((sum, record) => sum + (parseFloat(record.hours) || 0), 0);
      }

      setAttendanceData({
        period: periodData,
        records: attendanceRecords,
        totalHours,
        regularHours,
        ptoHours,
        sickHours,
        lunchHours,
        daysWorked: attendanceRecords.filter(r => r.hours > 0).length
      });

    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkConfirmationStatus = async () => {
    try {
      // Check confirmation status
      const { data: confData, error: confError } = await supabase
        .from('payroll_time_conf')
        .select('status, confirmed_at')
        .eq('payroll_number', currentPayrollNumber)
        .eq('employee_number', employeeData.employee_number)
        .limit(1);

      if (confError && confError.code !== 'PGRST116') {
        console.error('Error checking confirmation status:', confError);
        console.error('Query details:', {
          payroll_number: currentPayrollNumber,
          employee_number: employeeData.employee_number,
          error: confError
        });
      } else {
        console.log('Confirmation status query successful:', confData);
      }
      
      const confirmationRecord = confData && confData.length > 0 ? confData[0] : null;

      // Check if there are any unresolved questions
      console.log('Querying questions for:', {
        payroll_number: currentPayrollNumber,
        employee_number: employeeData.employee_number
      });
      
      const { data: questionData, error: questionError } = await supabase
        .from('payroll_time_question')
        .select('status, resolved_at, admin_response')
        .eq('payroll_number', currentPayrollNumber)
        .eq('employee_number', employeeData.employee_number)
        .order('created_at', { ascending: false })
        .limit(1);

      if (questionError) {
        console.error('Error checking question status:', questionError);
      } else {
        console.log('Question query successful:', questionData);
      }

      // Determine the current status
      console.log('Status determination:', {
        confirmationRecord,
        questionData,
        currentStatus: confirmationStatus
      });
      
      if (confirmationRecord && confirmationRecord.status === 'confirmed') {
        console.log('Setting status to confirmed');
        setConfirmationStatus('confirmed');
      } else if (questionData && questionData.length > 0) {
        const latestQuestion = questionData[0];
        console.log('Latest question data:', latestQuestion);
        
        if ((latestQuestion.status === 'resolved' && latestQuestion.resolved_at) || 
            (latestQuestion.status === 'answered' && latestQuestion.admin_response)) {
          // Question has been resolved/answered, show resolved status but allow confirmation
          console.log('Setting status to resolved - question resolved/answered');
          setConfirmationStatus('resolved');
        } else {
          // Question is still pending
          console.log('Setting status to disputed - question pending');
          setConfirmationStatus('disputed');
        }
      } else if (confirmationRecord) {
        console.log('Setting status from confirmation record:', confirmationRecord.status);
        setConfirmationStatus(confirmationRecord.status);
      } else {
        console.log('Setting status to pending - no questions or confirmation record');
        setConfirmationStatus('pending');
      }
    } catch (err) {
      console.error('Error checking confirmation status:', err);
    }
  };

  const getStoreCode = (storeName) => {
    if (!storeName) return '';
    
    const normalizedName = storeName.trim();
    const storeCodes = {
      'Main St': 'M',
      'Main': 'M',
      'Kissena Blvd': 'K',
      'Kissena': 'K',
      '39th Ave': '39TH',
      '39TH': '39TH',
      'Roosevelt Ave': 'R',
      'Roosevelt': 'R'
    };
    
    if (storeCodes[normalizedName]) {
      return storeCodes[normalizedName];
    }
    
    const lowerName = normalizedName.toLowerCase();
    for (const [storeName, code] of Object.entries(storeCodes)) {
      if (lowerName.includes(storeName.toLowerCase()) || storeName.toLowerCase().includes(lowerName)) {
        return code;
      }
    }
    
    return normalizedName.length > 4 ? normalizedName.substring(0, 4).toUpperCase() : normalizedName.toUpperCase();
  };

  const generateCalendarData = (records) => {
    if (!attendanceData || !attendanceData.period) return [];
    
    const calendarData = [];
    const weekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    
    // Generate dynamic date mapping based on actual period dates
    const startDate = new Date(attendanceData.period.start_date + 'T00:00:00');
    const endDate = new Date(attendanceData.period.end_date + 'T00:00:00');
    
    // Calculate all dates in the period (should be exactly 14 days)
    const periodDates = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      periodDates.push(new Date(d).toISOString().split('T')[0]);
    }
    
    // Map dates to calendar positions (Week 1: first 7 days, Week 2: next 7 days)
    const dateToCalendarPosition = {};
    periodDates.forEach((date, index) => {
      const week = Math.floor(index / 7) + 1;
      const dayIndex = index % 7;
      const dayName = weekDays[dayIndex];
      const position = `week${week}_${dayName}`;
      dateToCalendarPosition[date] = position;
    });
    
    // Generate the calendar structure using attendance records
    for (let week = 1; week <= 2; week++) {
      const weekData = [];
      for (const day of weekDays) {
        const fieldName = `week${week}_${day}`;
        
        // Find the actual date for this calendar position
        const actualDate = Object.keys(dateToCalendarPosition).find(
          date => dateToCalendarPosition[date] === fieldName
        );
        
        // Find attendance info for this date
        const attendanceInfo = actualDate ? records.find(item => item.date === actualDate) : null;
        const storeCode = getStoreCode(attendanceInfo?.store_name);
        const hours = attendanceInfo ? parseFloat(attendanceInfo.hours) || 0 : 0;
        
        weekData.push({
          day: day.charAt(0).toUpperCase() + day.slice(1),
          hours: hours,
          store: storeCode,
          storeName: attendanceInfo?.store_name || '',
          date: actualDate || ''
        });
      }
      calendarData.push(weekData);
    }
    
    return calendarData;
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setMessage('');

    try {
      const { error } = await supabase
        .from('payroll_time_conf')
        .upsert({
          payroll_number: currentPayrollNumber,
          employee_number: employeeData.employee_number,
          status: 'confirmed',
          confirmed_at: new Date().toISOString(),
          notes: 'Confirmed by employee'
        }, {
          onConflict: 'payroll_number,employee_number'
        });

      if (error) throw error;

      setConfirmationStatus('confirmed');
      setMessage('✅ Timesheet confirmed successfully!');
      
      // Send SMS notification to admin
      await sendSMSToAdmin('confirm', `Employee ${employeeData.employee_number} confirmed payroll ${currentPayrollNumber} timesheet.`);
      
    } catch (error) {
      console.error('Error confirming timesheet:', error);
      
      // Handle specific permission errors
      if (error.message.includes('403') || error.message.includes('permission')) {
        setMessage('✅ Confirmation recorded! (Note: Admin will process this manually)');
        setConfirmationStatus('confirmed');
      } else {
        setMessage('❌ Error confirming timesheet. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuestionSubmit = async () => {
    if (!question.trim()) {
      setMessage('Please enter a question before submitting.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const { data: questionData, error: questionError } = await supabase
        .from('payroll_time_question')
        .insert({
          payroll_number: currentPayrollNumber,
          employee_number: employeeData.employee_number,
          question: question.trim(),
          status: 'submitted'
        })
        .select()
        .single();

      if (questionError) throw questionError;

      const { error: confError } = await supabase
        .from('payroll_time_conf')
        .upsert({
          payroll_number: currentPayrollNumber,
          employee_number: employeeData.employee_number,
          status: 'disputed',
          notes: `Question submitted: ${question.substring(0, 100)}...`
        }, {
          onConflict: 'payroll_number,employee_number'
        });
        
      if (confError) {
        console.warn('Error updating confirmation status:', confError);
      }

      setConfirmationStatus('disputed');
      setShowQuestionDialog(false);
      setQuestion('');
      setMessage(`✅ Question submitted successfully! Question ID: ${questionData.question_no}`);

      await sendSMSToAdmin('question', `Employee ${employeeData.employee_number} asked about payroll ${currentPayrollNumber}: "${question.substring(0, 100)}..."`);

    } catch (error) {
      console.error('Error submitting question:', error);
      
      // Handle specific permission errors
      if (error.message.includes('403') || error.message.includes('permission')) {
        setMessage('✅ Question submitted! (Note: Admin will review this manually)');
        setConfirmationStatus('disputed');
        setShowQuestionDialog(false);
        setQuestion('');
      } else {
        setMessage('❌ Error submitting question. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendSMSToAdmin = async (type, messageText) => {
    console.log(`📱 SMS to Admin (${type}):`, messageText);
    
    try {
      // Send actual SMS to admin using the working SMS service
      const adminPhoneNumber = '+19299906651';
      const alertMessage = `🚨 STAFF ALERT: ${messageText}`;
      
      console.log('📝 Admin SMS payload being sent:', {
        employee_name: 'Admin',
        employee_number: 'admin', 
        phone: adminPhoneNumber,
        message: alertMessage,
        messageLength: alertMessage.length
      });
      
      // Send SMS using existing working SMS API
      console.log('📤 Sending admin SMS request to API...');
      const smsResponse = await fetch('https://paystub.roborx.workers.dev/api/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipients: [{
            employee_name: 'Admin',
            employee_number: 'admin',
            phone: adminPhoneNumber,
            message: alertMessage
          }],
          sendAll: false,
          skipPayrollValidation: true // Skip validation for admin notifications
        })
      });
      
      console.log('📊 Admin SMS API Response status:', smsResponse.status);
      console.log('📊 Admin SMS API Response headers:', Object.fromEntries(smsResponse.headers.entries()));
      
      if (smsResponse.ok) {
        const result = await smsResponse.json();
        console.log('✅ SMS sent to admin successfully:', result);
        console.log('📊 Admin SMS Results breakdown:', result.results);
      } else {
        const errorText = await smsResponse.text();
        console.warn('⚠️ Admin SMS API error:', smsResponse.status, errorText);
      }
    } catch (error) {
      console.error('⚠️ Admin SMS service error:', error.message);
      console.error('⚠️ Admin SMS service error stack:', error.stack);
    }
  };

  if (!currentPayrollNumber || loading) {
    return null;
  }

  if (!attendanceData) {
    return null;
  }

  const calendarData = generateCalendarData(attendanceData ? attendanceData.records : []);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="border-b pb-4 mb-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Current Timesheet - Payroll {currentPayrollNumber}
        </h2>
        <p className="text-gray-600">
          {attendanceData.period.period_label} • {attendanceData.daysWorked} days worked
        </p>
      </div>

      {/* Attendance Summary */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <span className="text-gray-600">Total Hours:</span>
          <span className="font-bold text-2xl text-gray-800">{attendanceData.totalHours.toFixed(2)}h</span>
        </div>
        <div className="border-t pt-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Timesheet Hours:</span>
            <span className="font-medium text-blue-600">{attendanceData.regularHours.toFixed(2)}h</span>
          </div>
          {attendanceData.ptoHours > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">PTO/Holiday:</span>
              <span className="font-medium text-purple-600">{attendanceData.ptoHours.toFixed(2)}h</span>
            </div>
          )}
          {attendanceData.sickHours > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Sick Leave:</span>
              <span className="font-medium text-red-600">{attendanceData.sickHours.toFixed(2)}h</span>
            </div>
          )}
          {attendanceData.lunchHours > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Lunch:</span>
              <span className="font-medium text-green-600">{attendanceData.lunchHours.toFixed(2)}h</span>
            </div>
          )}
          {/* Show multi-company indicator if employee has benefit hours from both companies */}
          {employeeData.employee_id_kf && employeeData.employee_id_koo && 
           (attendanceData.ptoHours > 0 || attendanceData.sickHours > 0 || attendanceData.lunchHours > 0) && (
            <div className="text-xs text-gray-400 mt-1 italic">
              * Combined from KF & KOO companies
            </div>
          )}
        </div>
      </div>

      {/* Calendar View Toggle */}
      <div className="mb-4">
        <button
          onClick={() => setExpandCalendar(!expandCalendar)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded flex items-center gap-2 transition-colors"
        >
          {expandCalendar ? '▼' : '▶'} View Calendar Details
        </button>
      </div>

      {/* Calendar View */}
      {expandCalendar && (
        <div className="mb-6">
          <p className="text-xs text-gray-600 mb-3 sm:mb-4">
            Pay Period: {attendanceData.period.period_label} | Store Codes: M=Main St, K=Kissena, 39TH=39th Ave, R=Roosevelt
          </p>
          {calendarData.map((week, weekIndex) => (
            <div key={weekIndex} className="mb-4 sm:mb-6">
              <h3 className="text-sm sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3">Week {weekIndex + 1}</h3>
              <div className="grid grid-cols-7 gap-0.5 sm:gap-2">
                {week.map((day, dayIndex) => (
                  <div 
                    key={dayIndex} 
                    className={`p-1 sm:p-3 rounded text-center transition-all ${
                      day.hours > 0 ? 'bg-blue-100 border border-blue-300' : 'bg-gray-100'
                    }`}
                    title={day.storeName ? `${day.storeName} - ${day.date}` : day.date}
                  >
                    <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">
                      {day.date ? day.date.split('-')[2] : ''}
                    </div>
                    <div className="font-semibold text-xs text-gray-700">{day.day}</div>
                    <div className="text-sm sm:text-lg font-bold text-gray-900 my-0.5 sm:my-1">{day.hours || '-'}</div>
                    {day.store && (
                      <div className="text-xs font-medium text-blue-700 bg-blue-200 rounded px-0.5 py-0.5">
                        {day.store}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          Timesheet Confirmation
        </h3>
        
        {message && (
          <div className={`mb-4 p-3 rounded ${
            message.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message}
          </div>
        )}

        {confirmationStatus === 'confirmed' ? (
          <div className="bg-green-100 border border-green-300 rounded p-3">
            <span className="text-green-800 font-medium">
              ✅ Timesheet confirmed on {new Date().toLocaleDateString()}
            </span>
          </div>
        ) : confirmationStatus === 'disputed' ? (
          <div className="bg-yellow-100 border border-yellow-300 rounded p-3">
            <span className="text-yellow-800 font-medium">
              ❓ Question submitted - waiting for admin response
            </span>
          </div>
        ) : confirmationStatus === 'resolved' ? (
          <div className="space-y-3">
            <div className="bg-green-100 border border-green-300 rounded p-3">
              <span className="text-green-800 font-medium">
                ✅ Your question has been resolved by admin
              </span>
            </div>
            <p className="text-gray-700 mb-4">
              Please review your timesheet above and confirm it's accurate, or ask another question if you notice any discrepancies.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleConfirm}
                disabled={isSubmitting}
                className={`px-6 py-2 rounded font-medium text-white ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500'
                }`}
              >
                {isSubmitting ? 'Confirming...' : '✅ Confirm'}
              </button>
              
              <button
                onClick={() => setShowQuestionDialog(true)}
                disabled={isSubmitting}
                className={`px-6 py-2 rounded font-medium text-white ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
                }`}
              >
                ❓ Ask Another Question
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-gray-700 mb-4">
              Please review your timesheet above and confirm it's accurate, or ask a question if you notice any discrepancies.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleConfirm}
                disabled={isSubmitting}
                className={`px-6 py-2 rounded font-medium text-white ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500'
                }`}
              >
                {isSubmitting ? 'Confirming...' : '✅ Confirm'}
              </button>
              
              <button
                onClick={() => setShowQuestionDialog(true)}
                disabled={isSubmitting}
                className={`px-6 py-2 rounded font-medium text-white ${
                  isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
                }`}
              >
                ❓ Question
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Question Dialog */}
      {showQuestionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
            <h4 className="text-lg font-semibold mb-4">Ask a Question about Your Timesheet</h4>
            
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Please describe any discrepancies or questions about your timesheet..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
            
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowQuestionDialog(false);
                  setQuestion('');
                  setMessage('');
                }}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              
              <button
                onClick={handleQuestionSubmit}
                disabled={isSubmitting || !question.trim()}
                className={`px-4 py-2 rounded text-white font-medium ${
                  isSubmitting || !question.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Question'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentPayrollConfirmation;